import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, PhoneCall, MessageCircle, MapPin, Building2, UserCheck } from "lucide-react";
import api from "@/lib/api";

export default function BookingSuccess() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [branch, setBranch] = useState(null);
  const [therapist, setTherapist] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/branches"),
      api.get("/therapists"),
      api.get("/bookings/mine").catch(() => ({ data: [] })),
    ]).then(([bRes, tRes, myBRes]) => {
      const bList = bRes.data || [];
      const tList = tRes.data || [];
      const foundB = (myBRes.data || []).find((x) => x.id === id);

      if (foundB) {
        setBooking(foundB);
        const assignedT = tList.find((t) => t.id === foundB.therapist_id);
        if (assignedT) setTherapist(assignedT);

        const assignedBranch = bList.find((br) => br.id === (foundB.branch_id || assignedT?.branch_id));
        if (assignedBranch) setBranch(assignedBranch);
        else setBranch(bList[0]);
      } else if (bList.length > 0) {
        setBranch(bList[0]);
      }
    }).catch(() => {});
  }, [id]);

  return (
    <div data-testid="booking-success" className="min-h-[75vh] flex items-center justify-center px-6 py-16">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-xl text-center w-full"
      >
        <div className="w-20 h-20 rounded-full border-2 border-emerald-400 bg-emerald-950/40 flex items-center justify-center mx-auto mb-6 shadow-xl shadow-emerald-950/50">
          <CheckCircle2 size={48} className="text-emerald-400" />
        </div>
        <div className="label-eyebrow mb-2 text-emerald-400 font-extrabold tracking-widest uppercase">RESERVATION CONFIRMED</div>
        <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold mb-3">
          You're all set!
        </h1>
        <p className="text-hsecondary text-sm leading-relaxed max-w-md mx-auto mb-4 font-normal">
          Your spa reservation is confirmed. Directions & pre-visit instructions have been prepared for your session.
        </p>

        <div className="text-xs text-white/40 mb-6">
          Reference Code: <span className="text-amber-300 font-mono font-bold">{id?.slice(0, 8)}</span>
        </div>

        {/* Assigned Branch & Direct Contact Info Card */}
        {branch && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 rounded-2xl border-2 border-amber-400/80 bg-gradient-to-r from-amber-950/50 via-black to-emerald-950/50 text-left space-y-4 shadow-2xl shadow-yellow-950/30 mb-8"
          >
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <span className="text-xs uppercase tracking-[0.2em] text-amber-300 font-extrabold flex items-center gap-1.5">
                <Building2 size={16} /> ASSIGNED BRANCH CONTACT
              </span>
              <span className="text-[10px] text-emerald-400 font-extrabold uppercase px-2.5 py-0.5 rounded bg-emerald-950 border border-emerald-400/40">
                {booking?.booking_type === "doorstep" ? "DOORSTEP HUB" : "IN-SPA LOCATION"}
              </span>
            </div>

            <div className="space-y-1">
              <h3 className="font-heading text-xl font-bold text-white">{branch.name}</h3>
              {branch.address && (
                <p className="text-xs text-white/70 flex items-center gap-1.5">
                  <MapPin size={14} className="text-amber-400 shrink-0" /> {branch.address}, {branch.city}
                </p>
              )}
              {therapist && (
                <p className="text-xs text-emerald-300 font-bold flex items-center gap-1.5 pt-1">
                  <UserCheck size={14} className="text-emerald-400" /> Assigned Therapist: {therapist.display_name}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <a
                href={`tel:${branch.phone || "+919560656913"}`}
                className="gold-btn py-3 px-4 rounded-xl text-xs font-extrabold uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg"
              >
                <PhoneCall size={15} /> Call {branch.phone || "+91 9560656913"}
              </a>
              <a
                href={`https://wa.me/${(branch.phone2 || branch.phone || "919560656913").replace(/[^0-9]/g, "")}?text=Hi%20${encodeURIComponent(branch.name)},%20I%20have%20a%20confirmed%20booking%20(Ref:%20${id?.slice(0, 8)}).`}
                target="_blank"
                rel="noreferrer"
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold py-3 px-4 rounded-xl text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg transition-colors"
              >
                <MessageCircle size={15} /> WhatsApp Branch
              </a>
            </div>
          </motion.div>
        )}

        <div className="flex gap-3 justify-center">
          <Link to="/" className="ghost-btn px-6 py-3 rounded-xl text-xs uppercase font-extrabold tracking-wider">Back to home</Link>
          <Link to="/dashboard/appointments" className="gold-btn px-6 py-3 rounded-xl text-xs uppercase font-extrabold tracking-wider">My appointments</Link>
        </div>
      </motion.div>
    </div>
  );
}
